/**
 * TrainingCaptureCoordinator — singleton that owns one LbxTrainingCapture
 * SDK instance per signed-in voice-agent session and bridges its lifecycle
 * to the voice-agent loop (LinkLiveSession).
 *
 * Mirrors the iOS coordinator at:
 *   linkbox-ios/Link Box AI/Services/Training/LbxTrainingCoordinator.swift
 * and the Android equivalent at:
 *   linkbox-android/app/src/main/java/com/linkbox/ai/services/training/LbxTrainingCaptureService.kt
 *
 * Pipeline note: this is the *wardrobe-platform SDK* capture pathway that
 * POSTs to `/api/v1/training/ingest`. It is intentionally separate from
 * the in-house `TrainingCaptureService` in this directory, which uploads
 * to the Supabase Edge Function `training-ingest`. Both can coexist;
 * neither is the canonical winner yet.
 *
 * Gates (ALL must pass before any mic/camera is opened):
 *   1. Feature flag — env `NEXT_PUBLIC_LBX_TRAINING_CAPTURE_ENABLED === '1'`
 *      OR `localStorage["lbx.training.capture.enabled"] === '1'`.
 *   2. Consent — `getTrainingConsent()` returns `{ training: true,
 *      (audio || camera): true }`.
 *   3. Identity — `householdId` AND `memberId` resolve to non-empty
 *      strings. Sources (in order):
 *        a. localStorage["lbx.training.household_id"] /
 *           localStorage["lbx.training.member_id"] — manual test override
 *           kept as a dev escape hatch.
 *        b. `configureIdentity({householdId, memberId})` — wired from
 *           the layout sign-in hook after the
 *           `get_or_create_self_member` RPC returns. This is the
 *           production path; mirrors the iOS + Android coordinators.
 *   4. JWT — Supabase session token available (same pattern as
 *      `lib/agent/tools/personalization-tool.ts`).
 *
 * If any gate fails, `beginVoiceSession` is a silent no-op. The voice
 * agent flow continues unaffected.
 */

import { createClient } from '@/lib/supabase/client'
import { LbxTrainingCapture } from './lbx-training'
import {
  CURRENT_CONSENT_VERSION,
  canCaptureWithGrants,
  getTrainingConsent,
  type TrainingConsentGrants,
} from './consent-store'

const FEATURE_FLAG_STORAGE_KEY = 'lbx.training.capture.enabled'
const HOUSEHOLD_ID_OVERRIDE_KEY = 'lbx.training.household_id'
const MEMBER_ID_OVERRIDE_KEY = 'lbx.training.member_id'

const WARDROBE_URL =
  process.env.NEXT_PUBLIC_LBX_WARDROBE_URL || 'http://localhost:8080'

const INGEST_PATH = '/api/v1/training/ingest'

export interface BeginVoiceSessionOptions {
  agentId?: string
  context?: string
  lang?: string
}

interface ResolvedIdentity {
  householdId: string
  memberId: string
}

class TrainingCaptureCoordinator {
  private capture: LbxTrainingCapture | null = null
  private starting = false
  private activeAgentId: string | null = null
  private configuredIdentity: ResolvedIdentity | null = null

  // ── Gates ────────────────────────────────────────────────────────────

  /**
   * Read the feature flag. False by default — the SDK pathway stays dark
   * until the host explicitly enables it.
   */
  isEnabled(): boolean {
    if (process.env.NEXT_PUBLIC_LBX_TRAINING_CAPTURE_ENABLED === '1') {
      return true
    }
    if (typeof window === 'undefined') return false
    try {
      return window.localStorage.getItem(FEATURE_FLAG_STORAGE_KEY) === '1'
    } catch (err) {
      return false
    }
  }

  /**
   * Identity resolution. Order:
   *   1. localStorage overrides — manual dev/test escape hatch.
   *   2. `configureIdentity(...)` value — production path, wired
   *      from the layout sign-in hook after the
   *      `get_or_create_self_member` RPC.
   * Returns null when nothing is configured (signed-out / pre-hydrate).
   */
  private resolveIdentity(): ResolvedIdentity | null {
    if (typeof window !== 'undefined') {
      try {
        const household = window.localStorage.getItem(HOUSEHOLD_ID_OVERRIDE_KEY)
        const member = window.localStorage.getItem(MEMBER_ID_OVERRIDE_KEY)
        if (household && member) {
          return { householdId: household, memberId: member }
        }
      } catch (err) {
        // localStorage unreadable — fall through to the configured identity.
      }
    }
    return this.configuredIdentity
  }

  /**
   * Wire the user's `(household_id, member_id)` after sign-in. Called
   * by the layout-level hook once `fetchOrCreateSelfMember()` resolves.
   * Pass `null` on sign-out to clear the identity so any subsequent
   * `beginVoiceSession` call no-ops.
   */
  configureIdentity(identity: ResolvedIdentity | null): void {
    this.configuredIdentity = identity
  }

  /**
   * `true` once the layout sign-in hook has wired identity (or the
   * dev-only localStorage override is set). Consent UI uses this to
   * choose between toggle controls and a "sign in to manage consent"
   * empty state.
   */
  identityConfigured(): boolean {
    return this.resolveIdentity() !== null
  }

  // ── Public lifecycle ────────────────────────────────────────────────

  /**
   * Begin a capture session for the voice agent. Silent no-op if any of
   * the four gates above fail (feature-flag / consent / identity / JWT).
   *
   * Safe to call when a session is already running — duplicate calls
   * resolve immediately without starting a second SDK instance.
   */
  async beginVoiceSession(opts: BeginVoiceSessionOptions = {}): Promise<void> {
    if (this.capture || this.starting) return
    if (!this.isEnabled()) return

    const grants = getTrainingConsent()
    if (!canCaptureWithGrants(grants)) return

    const identity = this.resolveIdentity()
    if (!identity) return

    const supabase = createClient()
    let accessToken: string | null = null
    try {
      const { data } = await supabase.auth.getSession()
      accessToken = data?.session?.access_token ?? null
    } catch (err) {
      accessToken = null
    }
    if (!accessToken) return

    this.starting = true
    const ingestUrl = `${WARDROBE_URL}${INGEST_PATH}`
    const capture = new LbxTrainingCapture({
      ingestUrl,
      householdId: identity.householdId,
      memberId: identity.memberId,
      platform: 'web',
      getConsent: () => mapToSdkConsent(getTrainingConsent()),
    })

    try {
      await capture.startSession({
        agentId: opts.agentId ?? 'linklive',
        context: opts.context ?? 'voice_query',
        lang: opts.lang ?? (typeof navigator !== 'undefined' ? navigator.language : 'en-US'),
      })
      await capture.startCapture()
      this.capture = capture
      this.activeAgentId = opts.agentId ?? 'linklive'
    } catch (err) {
      // SDK raised (consent revoked between gates, mic denied, REST
      // rejected). Drop the instance so the next call retries cleanly.
      this.capture = null
      this.activeAgentId = null
    } finally {
      this.starting = false
    }
  }

  /**
   * End the active capture session, flushing any trailing partial chunk.
   * Safe to call when no session is running.
   */
  async endVoiceSession(): Promise<void> {
    const capture = this.capture
    if (!capture) return
    this.capture = null
    this.activeAgentId = null
    try {
      await capture.stopCapture()
    } catch (err) {
      // best-effort cleanup
    }
    try {
      await capture.endSession()
    } catch (err) {
      // best-effort cleanup
    }
  }

  /**
   * Test-only / debug accessor — returns null in normal flows because the
   * coordinator owns the SDK instance privately.
   */
  hasActiveSession(): boolean {
    return this.capture !== null
  }
}

// SDK consent shape uses { audio, camera, training, version, bipaAck }
// — identical field names but a distinct type alias so the store can
// evolve independently of the upstream SDK contract.
function mapToSdkConsent(g: TrainingConsentGrants) {
  return {
    audio: g.audio,
    camera: g.camera,
    training: g.training,
    version: g.version || CURRENT_CONSENT_VERSION,
    bipaAck: g.bipaAck,
  }
}

const instance = new TrainingCaptureCoordinator()

export function getTrainingCaptureCoordinator(): TrainingCaptureCoordinator {
  return instance
}

export type { TrainingCaptureCoordinator }
